import { ArrowPathIcon, BanknotesIcon, ChartBarSquareIcon, CursorArrowRaysIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import DashboardChart from "./DashboardChart";
import { formatCurrency } from "../lib/affiliateAnalytics";

export default function DashboardCards({ analytics, loading, onRefresh }) {
  const totals = analytics?.totals;
  const level = analytics?.level;

  return (
    <div className="space-y-6">
      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1.4fr_1fr]">
        <div className="overflow-hidden rounded-[32px] border border-white/10 bg-[linear-gradient(135deg,rgba(29,217,175,0.14),rgba(84,163,255,0.08),rgba(255,184,77,0.12))] p-6 shadow-[0_30px_90px_rgba(0,0,0,0.3)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-[var(--aff-muted)]">
                Overview
              </div>
              <h2 className="mt-2 text-3xl font-black text-white">
                Real affiliate rewards and turnover
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-300">
                Approved referred deposits now create real locked affiliate bonus, and your dashboard balance comes from unlocked commission instead of estimate-only share math.
              </p>
            </div>

            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex items-center gap-2 rounded-2xl border border-white/12 bg-white/[0.06] px-4 py-2 text-sm font-semibold text-white hover:bg-white/[0.1]"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Refresh
            </button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              icon={BanknotesIcon}
              label="Unlocked balance"
              value={loading ? "..." : formatCurrency(totals?.unlockedBonus)}
              hint="Ready for affiliate withdrawal"
            />
            <SummaryCard
              icon={ChartBarSquareIcon}
              label="Locked bonus"
              value={loading ? "..." : formatCurrency(totals?.lockedBonus)}
              hint="Unlocks after 100x turnover"
            />
            <SummaryCard
              icon={CursorArrowRaysIcon}
              label="Commission earned"
              value={loading ? "..." : formatCurrency(totals?.totalCommission)}
              hint={loading ? "" : `Level ${level?.level || 1} partner`}
            />
            <SummaryCard
              icon={UserPlusIcon}
              label="Registrations"
              value={loading ? "..." : String(totals?.registrations || 0)}
              hint={loading ? "" : `${totals?.ftdsCount || 0} first time depositors`}
            />
          </div>
        </div>

        <div className="rounded-[32px] border border-white/10 bg-[rgba(11,18,31,0.92)] p-6 shadow-[0_30px_80px_rgba(0,0,0,0.28)]">
          <div className="text-xs uppercase tracking-[0.28em] text-[var(--aff-muted)]">
            Snapshot
          </div>
          <div className="mt-3 space-y-4">
            <StatLine label="Withdrawable now" value={loading ? "..." : formatCurrency(totals?.balance)} />
            <StatLine label="Pending withdrawals" value={loading ? "..." : formatCurrency(totals?.pendingWithdrawals)} />
            <StatLine label="Total withdrawn" value={loading ? "..." : formatCurrency(totals?.totalWithdrawn)} />
            <StatLine label="FTD volume" value={loading ? "..." : formatCurrency(totals?.ftdsSum)} />
            <StatLine label="Tracked player turnover" value={loading ? "..." : formatCurrency(totals?.totalTurnover)} />
          </div>
        </div>
      </section>

      <DashboardChart data={analytics?.timeline || []} loading={loading} />
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-[rgba(10,16,28,0.72)] p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
          <Icon className="h-5 w-5 text-emerald-300" />
        </div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.18em] text-[var(--aff-muted)]">{label}</div>
          <div className="truncate text-xl font-black text-white">{value}</div>
          <div className="text-xs text-slate-400">{hint}</div>
        </div>
      </div>
    </div>
  );
}

function StatLine({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-2xl border border-white/8 bg-white/[0.04] px-4 py-3">
      <span className="text-sm text-slate-400">{label}</span>
      <span className="text-base font-bold text-white">{value}</span>
    </div>
  );
}
